// Industry creative data for the shoppable UGC animated demos.
// Each entry maps to an /industries/[slug] page and drives the
// IndustryShoppableCreative component.
//
// Images reference Unsplash directly (already in next.config.ts remotePatterns).
// Photo IDs sourced from the Industry Creatives Plan v2 (16 verticals).

const U = (id: string, w = 900) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`
const P = (id: string) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=300&h=300&q=70`

export type CreativePin = {
  /** Position as % of image width/height */
  x: number
  y: number
  name: string
  price: string
  img: string
}

export type IndustryCreative = {
  slug: string
  no: string
  name: string
  tagline: string
  hero: string
  brand: string
  hashtag: string
  postCount: string
  tags: string[]
  pins: CreativePin[]
  widget: string
  cta: string
  embeds: string[]
  liftNum: string
  liftLabel: string
  shotList: string
}

export const industryCreatives: IndustryCreative[] = [
  {
    no: '01',
    slug: 'fashion-and-apparels',
    name: 'Fashion + apparel',
    tagline: 'Shoppable lookbooks and on-model UGC where every piece the creator wears is one tap to buy.',
    hero: U('1483985988355-763728e1935b'),
    brand: 'Maison Faye',
    hashtag: 'MaisonFaye',
    postCount: '1,240',
    tags: ['Coat', 'Knit top', 'Tote'],
    pins: [
      { x: 50, y: 38, name: 'Oversized wool coat', price: '$248', img: P('1539533018447-63fcce2678e3') },
      { x: 34, y: 64, name: 'Ribbed knit top', price: '$72', img: P('1576566588028-4147f3842f27') },
      { x: 70, y: 70, name: 'Leather tote', price: '$190', img: P('1584917865442-de89df76afd3') },
    ],
    widget: 'Shoppable Gallery — on-model lookbook',
    cta: 'Add to bag',
    embeds: ["PDP 'Shop the look'", 'Homepage hero', 'IG bio link'],
    liftNum: '+34%',
    liftLabel: 'add-to-cart rate',
    shotList: 'Full-length street-style reel, creator mid-stride, 2–4 hero garments clearly in frame.',
  },
  {
    no: '02',
    slug: 'beauty-and-cosmetics',
    name: 'Beauty + skincare',
    tagline: 'Routine, swatch and before/after clips where each step links to its exact SKU and shade.',
    hero: U('1531123897727-8f129e1688ce'),
    brand: 'Lumi Skin',
    hashtag: 'LumiGlow',
    postCount: '3,180',
    tags: ['Serum', 'Moisturiser', 'Lip oil'],
    pins: [
      { x: 60, y: 34, name: 'Glow drops serum', price: '$46', img: P('1620916566398-39f1143ab7be') },
      { x: 36, y: 30, name: 'Barrier moisturiser', price: '$32', img: P('1556228720-195a672e8a03') },
      { x: 64, y: 66, name: 'Tinted lip oil — Coral', price: '$24', img: P('1586495777744-4413f21062fa') },
    ],
    widget: 'Shoppable Video — tappable routine timeline',
    cta: 'Shop shade',
    embeds: ['PDP routine block', 'Reels', 'Email hero'],
    liftNum: '+41%',
    liftLabel: 'product page views',
    shotList: 'GRWM or skin-finish portrait; tag the serum, moisturiser and lip in sequence.',
  },
  {
    no: '03',
    slug: 'accessories-and-jewelery',
    name: 'Accessories + jewellery',
    tagline: 'Macro and lifestyle galleries where the necklace, earring and ring each carry their own pin.',
    hero: U('1611652022419-a9419f74343d'),
    brand: 'Vela Studio',
    hashtag: 'VelaOnYou',
    postCount: '890',
    tags: ['Necklace', 'Earring', 'Ring'],
    pins: [
      { x: 52, y: 44, name: 'Layered pearl necklace', price: '$320', img: P('1599643478518-a784e5dc4c8f') },
      { x: 30, y: 34, name: 'Gold stud earring', price: '$95', img: P('1535632066927-ab7c9ab60908') },
      { x: 70, y: 64, name: 'Thin stacking ring', price: '$60', img: P('1605100804763-247f67b3557e') },
    ],
    widget: 'Shoppable Image — zoom + hotspot',
    cta: 'View piece',
    embeds: ['PDP detail gallery', 'Lookbook', 'Pinterest'],
    liftNum: '+27%',
    liftLabel: 'avg. order value',
    shotList: 'Close worn-on-body shot; one focal piece, two supporting, clean warm light.',
  },
  {
    no: '04',
    slug: 'home-and-lifestyle',
    name: 'Home + lifestyle',
    tagline: 'In-room visualisation and review UGC where every styled object is shoppable in context.',
    hero: U('1543269664-56d93c1b41a6'),
    brand: 'Noma Home',
    hashtag: 'MyNomaHome',
    postCount: '2,050',
    tags: ['Vase', 'Candle', 'Throw'],
    pins: [
      { x: 44, y: 46, name: 'Ceramic ribbed vase', price: '$58', img: P('1578500494198-246f612d3b3d') },
      { x: 24, y: 62, name: 'Soy travel candle', price: '$34', img: P('1603006905003-be475563bc59') },
      { x: 74, y: 58, name: 'Wool boucle throw', price: '$120', img: P('1600369671236-e74521d4b6ad') },
    ],
    widget: 'UGC Widget — in-room gallery',
    cta: 'Shop the room',
    embeds: ["PDP 'In real homes'", 'Category landers', 'Email'],
    liftNum: '+33%',
    liftLabel: 'time on page',
    shotList: 'Real customer room, daylight, 3 decor pieces with clear silhouettes.',
  },
  {
    no: '05',
    slug: 'furniture',
    name: 'Furniture',
    tagline: 'High-AOV social proof at the PDP — styled room scenes turned into a shoppable set.',
    hero: U('1493663284031-b7e3aefcae8e'),
    brand: 'Norr Living',
    hashtag: 'NorrAtHome',
    postCount: '640',
    tags: ['Sofa', 'Floor lamp', 'Side table'],
    pins: [
      { x: 48, y: 60, name: 'Velvet 3-seat sofa', price: '$1,290', img: P('1555041469-a586c61ea9bc') },
      { x: 22, y: 42, name: 'Arc floor lamp', price: '$240', img: P('1507473885765-e6ed057f782c') },
      { x: 80, y: 66, name: 'Oak side table', price: '$180', img: P('1532372320572-cda25653a26d') },
    ],
    widget: 'Shop-the-Room — scene hotspots',
    cta: 'Add to cart',
    embeds: ["PDP 'Get the look'", 'Catalog', 'Paid social'],
    liftNum: '+52%',
    liftLabel: 'cross-sell revenue',
    shotList: 'Wide styled room, 3–5 buyable pieces, golden-hour light.',
  },
  {
    no: '06',
    slug: 'baby-and-kids',
    name: 'Baby + kids',
    tagline: 'Parent reviews, safety stories and growth journeys that tag the gear actually in use.',
    hero: U('1476703993599-0035a21b17a9'),
    brand: 'Tiny Co',
    hashtag: 'TinyCoFam',
    postCount: '1,510',
    tags: ['Stroller', 'Wooden toy', 'Onesie'],
    pins: [
      { x: 38, y: 54, name: 'Lightweight stroller', price: '$390', img: P('1515488042361-ee00e0ddd4e4') },
      { x: 64, y: 40, name: 'Wooden stacker toy', price: '$28', img: P('1558877385-81a1c7e67d72') },
      { x: 70, y: 70, name: 'Organic cotton onesie', price: '$22', img: P('1522771930-78848d9293e8') },
    ],
    widget: 'UGC Widget — parent review wall',
    cta: 'Shop for baby',
    embeds: ['PDP review block', 'Registry pages', 'Email'],
    liftNum: '+36%',
    liftLabel: 'review engagement',
    shotList: 'Candid parent-and-child moment; gear visible and in real use.',
  },
  {
    no: '07',
    slug: 'pet-care',
    name: 'Pet care',
    tagline: 'Pet-parent UGC — before/after and nutrition journeys that tag food, leash and bed.',
    hero: U('1450778869180-41d0601e046e'),
    brand: 'Barkwell',
    hashtag: 'BarkwellPack',
    postCount: '2,770',
    tags: ['Food', 'Leash', 'Bed'],
    pins: [
      { x: 46, y: 48, name: 'Grain-free dog food', price: '$54', img: P('1589924691995-400dc9ecc119') },
      { x: 26, y: 60, name: 'Rope walking leash', price: '$26', img: P('1601758228041-f3b2795255f1') },
      { x: 74, y: 62, name: 'Orthopaedic pet bed', price: '$89', img: P('1591946614720-90a587da4a36') },
    ],
    widget: 'Shoppable Video — nutrition journey',
    cta: 'Add to cart',
    embeds: ['PDP', 'Subscription pages', 'IG reels'],
    liftNum: '+44%',
    liftLabel: 'subscription sign-ups',
    shotList: 'Happy pet moment at home or on a walk; products naturally in frame.',
  },
  {
    no: '08',
    slug: 'health-and-wellness',
    name: 'Health + wellness',
    tagline: 'Supplement, sleep and recovery UGC where each routine product links to its regimen.',
    hero: U('1518611012118-696072aa579a'),
    brand: 'Vitae',
    hashtag: 'VitaeDaily',
    postCount: '1,930',
    tags: ['Supplement', 'Daily vitamin', 'Tea'],
    pins: [
      { x: 42, y: 44, name: 'Magnesium glycinate', price: '$36', img: P('1607619056574-7b8d3ee536b2') },
      { x: 64, y: 38, name: 'Daily multivitamin', price: '$28', img: P('1550572017-edd951b55104') },
      { x: 60, y: 68, name: 'Sleep blend tea', price: '$18', img: P('1597481499750-3e6b22637e12') },
    ],
    widget: 'Shoppable Video — routine builder',
    cta: 'Build routine',
    embeds: ['PDP regimen block', 'Quiz results', 'Email'],
    liftNum: '+39%',
    liftLabel: 'bundle conversion',
    shotList: 'Movement, recovery or morning-routine clip; tag the supplement, vitamin and tea.',
  },
  {
    no: '09',
    slug: 'food-and-beverage',
    name: 'Food + beverage',
    tagline: 'Recipe, tasting and unboxing UGC where gifting and pantry items are tap-to-shop.',
    hero: U('1556910103-1c02745aae4d'),
    brand: 'Brindle',
    hashtag: 'BrindleTable',
    postCount: '4,120',
    tags: ['Coffee', 'Chocolate', 'Olive oil'],
    pins: [
      { x: 36, y: 46, name: 'Single-origin coffee', price: '$22', img: P('1559056199-641a0ac8b55e') },
      { x: 62, y: 40, name: 'Dark chocolate bar', price: '$9', img: P('1511381939415-e44015466834') },
      { x: 66, y: 68, name: 'Cold-press olive oil', price: '$28', img: P('1474979266404-7eaacbcd87c5') },
    ],
    widget: 'Shoppable Video — recipe tags',
    cta: 'Add to basket',
    embeds: ['PDP', 'Recipe blog', 'Gifting landers'],
    liftNum: '+47%',
    liftLabel: 'gift-set attach rate',
    shotList: 'Tasting or cook-along moment with people; tag 3 hero pantry products.',
  },
  {
    no: '10',
    slug: 'hospitality',
    name: 'Hospitality',
    tagline: 'Property feeds and guest stories where the suite, cabana and spa are bookable inline.',
    hero: U('1571003123894-1f0594d2b5d9'),
    brand: 'Costa Verde',
    hashtag: 'StayCostaVerde',
    postCount: '760',
    tags: ['Suite', 'Cabana', 'Spa'],
    pins: [
      { x: 50, y: 48, name: 'Ocean-view suite / night', price: '$420', img: P('1611892440504-42a792e24d32') },
      { x: 26, y: 64, name: 'Poolside cabana', price: '$90', img: P('1571896349842-33c89424de2d') },
      { x: 74, y: 58, name: 'Sunset spa package', price: '$160', img: P('1540555700478-4be289fbecef') },
    ],
    widget: 'Shoppable Video — book inline',
    cta: 'Check dates',
    embeds: ['Booking page', 'Destination guide', 'IG stories'],
    liftNum: '+38%',
    liftLabel: 'booking conversions',
    shotList: 'Golden-hour property wide shot; tag the room type, cabana and experience.',
  },
  {
    no: '11',
    slug: 'travel-and-experiences',
    name: 'Travel + experiences',
    tagline: 'Trip reels, itineraries and packing lists where every kit item is shoppable.',
    hero: U('1517760444937-f6397edcbbcd'),
    brand: 'Wayfarer',
    hashtag: 'WayfarerKit',
    postCount: '1,360',
    tags: ['Backpack', 'Camera', 'Bottle'],
    pins: [
      { x: 48, y: 50, name: '40L travel backpack', price: '$180', img: P('1553062407-98eeb64c6a62') },
      { x: 28, y: 40, name: 'Compact mirrorless camera', price: '$899', img: P('1526170375885-4d8ecf77b99f') },
      { x: 72, y: 62, name: 'Insulated bottle', price: '$38', img: P('1602143407151-7111542de6e8') },
    ],
    widget: 'Shoppable Video — packing-list tags',
    cta: 'Shop the kit',
    embeds: ['PDP', 'Itinerary blog', 'Email reels'],
    liftNum: '+31%',
    liftLabel: 'kit attach rate',
    shotList: 'On-location adventure reel; tag the packed gear the creator is using.',
  },
  {
    no: '12',
    slug: 'consumer-electronics',
    name: 'Consumer electronics',
    tagline: 'Unboxing and tutorial UGC where the watch, buds and phone each open their spec page.',
    hero: U('1598550476439-6847785fcea6'),
    brand: 'Kvant',
    hashtag: 'KvantSetup',
    postCount: '2,240',
    tags: ['Smartwatch', 'Earbuds', 'Phone'],
    pins: [
      { x: 42, y: 46, name: 'Series 6 smartwatch', price: '$329', img: P('1546868871-7041f2a55e12') },
      { x: 66, y: 40, name: 'Wireless earbuds Pro', price: '$179', img: P('1606220588913-b3aacb4d2f46') },
      { x: 36, y: 66, name: 'Edge phone 256GB', price: '$699', img: P('1511707171634-5f897ff02aa9') },
    ],
    widget: 'Shoppable Video — unboxing tags',
    cta: 'See specs',
    embeds: ['PDP comparison', 'Bundle pages', 'Launch email'],
    liftNum: '+29%',
    liftLabel: 'bundle attach rate',
    shotList: 'Unboxing or desk-setup clip; 3 devices, screens and badges visible.',
  },
  {
    no: '13',
    slug: 'sporting-goods',
    name: 'Sporting goods',
    tagline: 'Athlete and community UGC where the kit on the athlete is tap-to-shop, mid-rep.',
    hero: U('1517836357463-d25dfeac3438'),
    brand: 'Forge Athletic',
    hashtag: 'ForgeCrew',
    postCount: '3,510',
    tags: ['Trainers', 'Tights', 'Lifting belt'],
    pins: [
      { x: 50, y: 70, name: 'Grip training shoe', price: '$140', img: P('1542291026-7eec264c27ff') },
      { x: 44, y: 46, name: 'Compression tights', price: '$78', img: P('1506629082955-511b1aa562c8') },
      { x: 62, y: 56, name: 'Leather lifting belt', price: '$65', img: P('1517963879433-6ad2b056d712') },
    ],
    widget: 'Shoppable Video — rep-synced tags',
    cta: 'Add to kit',
    embeds: ['PDP', 'Training-plan pages', 'Athlete reels'],
    liftNum: '+45%',
    liftLabel: 'click-through rate',
    shotList: 'Dynamic training moment, athlete in full kit, 3 gear items in frame.',
  },
  {
    no: '14',
    slug: 'automobiles',
    name: 'Automobiles',
    tagline: 'Ownership reels, road-trips and EV-charging UGC where parts and accessories are tagged.',
    hero: U('1494976388531-d1058494cdd8'),
    brand: 'Atlas Motors',
    hashtag: 'AtlasOwners',
    postCount: '980',
    tags: ['Roof box', 'Tyres', 'EV charger'],
    pins: [
      { x: 50, y: 50, name: 'Aero roof box', price: '$540', img: P('1503376780353-7e6692767b70') },
      { x: 28, y: 64, name: 'All-season tyre set', price: '$720', img: P('1568605117036-5fe5e7bab0b7') },
      { x: 74, y: 56, name: 'Home EV charger', price: '$480', img: P('1593941707882-a5bba14938c7') },
    ],
    widget: 'Shoppable Video — ownership reel',
    cta: 'Configure',
    embeds: ['PDP accessories', 'Owner hub', 'Dealer pages'],
    liftNum: '+24%',
    liftLabel: 'accessory attach rate',
    shotList: 'Road-trip or ownership reel; tag accessories, tyres and charging gear.',
  },
  {
    no: '15',
    slug: 'edtech',
    name: 'EdTech',
    tagline: 'Outcome stories, streaks and "I got the job" UGC where courses link straight to enrol.',
    hero: U('1522202176988-66273c2fd55f'),
    brand: 'Lumen Learn',
    hashtag: 'LumenWins',
    postCount: '1,670',
    tags: ['Course', 'Workbook', 'Toolkit'],
    pins: [
      { x: 46, y: 48, name: 'Full-stack course', price: '$240', img: P('1517336714731-489689fd1ca8') },
      { x: 28, y: 60, name: 'Printed workbook', price: '$45', img: P('1544716278-ca5e3f4abd8c') },
      { x: 72, y: 42, name: 'Career toolkit', price: '$90', img: P('1454165804606-c3d57bc86b40') },
    ],
    widget: 'UGC Widget — outcome story wall',
    cta: 'Start learning',
    embeds: ['Course PDP', 'Outcome page', 'Email'],
    liftNum: '+42%',
    liftLabel: 'enrolment rate',
    shotList: 'Learner outcome or study-streak clip; tag the course, kit and resource.',
  },
  {
    no: '16',
    slug: 'b2b-and-saas',
    name: 'B2B + SaaS',
    tagline: 'Logos, quotes and case-study UGC where proof points link to the relevant product page.',
    hero: U('1521737604893-d14cc237f11d'),
    brand: 'Northpeak',
    hashtag: 'NorthpeakProof',
    postCount: '410',
    tags: ['Platform', 'Pro plan', 'Case study'],
    pins: [
      { x: 46, y: 46, name: 'Analytics platform', price: '$0', img: P('1551288049-bebda4e38f71') },
      { x: 28, y: 62, name: 'Pro plan / seat', price: '$49', img: P('1600880292203-757bb62b4baf') },
      { x: 72, y: 54, name: 'Growth case study', price: 'Free', img: P('1460925895917-afdab827c52f') },
    ],
    widget: 'Social Reviews — proof wall',
    cta: 'Book a demo',
    embeds: ['Pricing page', 'Case-study hub', 'Sales decks'],
    liftNum: '+28%',
    liftLabel: 'demo requests',
    shotList: 'Customer testimonial or team clip; tag the product, plan and case study.',
  },
]

export function getIndustryCreative(slug: string): IndustryCreative | undefined {
  return (
    industryCreatives.find((c) => c.slug === slug) ||
    industryCreatives.find((c) =>
      slug.split('-').some((part) => c.slug.split('-').includes(part)),
    )
  )
}
